import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * StatusFilterCard - a clickable KPI-style tile used on the OrderTrackerPage
 * to filter the orders table by status. Mirrors the visual language of
 * KPICard (label + value + optional accent dot) but renders as a <button>
 * with a clear `selected` state.
 *
 * Selected state is communicated three ways for accessibility:
 *   - aria-pressed
 *   - thicker accent-coloured border (no layout shift; the unselected card
 *     reserves the same border width)
 *   - subtle ring around the card
 *
 * Sizes are kept tight (smaller than KPICard) because seven of these sit
 * in a single row above the orders table.
 */

export type StatusFilterAccent =
  | 'brand'
  | 'success'
  | 'warning'
  | 'info'
  | 'clay'
  | 'sand'
  | 'muted';

export interface StatusFilterCardProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  label: React.ReactNode;
  value: React.ReactNode;
  accent?: StatusFilterAccent;
  selected?: boolean;
  loading?: boolean;
}

const accentByKey: Record<
  StatusFilterAccent,
  { dot: string; border: string; ring: string; text: string }
> = {
  brand: {
    dot: 'bg-brand-500',
    border: 'border-brand-500',
    ring: 'ring-brand-200',
    text: 'text-brand-700',
  },
  success: {
    dot: 'bg-success-500',
    border: 'border-success-500',
    ring: 'ring-success-200',
    text: 'text-success-700',
  },
  warning: {
    dot: 'bg-warning-500',
    border: 'border-warning-500',
    ring: 'ring-warning-200',
    text: 'text-warning-700',
  },
  info: {
    dot: 'bg-info-500',
    border: 'border-info-500',
    ring: 'ring-info-200',
    text: 'text-info-700',
  },
  clay: {
    dot: 'bg-clay-500',
    border: 'border-clay-500',
    ring: 'ring-clay-200',
    text: 'text-clay-700',
  },
  sand: {
    // Sand is a neutral; pick a mid-saturation tone (sand-400) so the dot
    // reads as a soft yellow against the white card background.
    dot: 'bg-sand-400',
    border: 'border-sand-400',
    ring: 'ring-sand-200',
    text: 'text-sand-700',
  },
  muted: {
    dot: 'bg-sand-500',
    border: 'border-sand-500',
    ring: 'ring-sand-200',
    text: 'text-text-secondary',
  },
};

export function StatusFilterCard({
  label,
  value,
  accent = 'brand',
  selected = false,
  loading = false,
  className,
  type,
  ...props
}: StatusFilterCardProps) {
  const a = accentByKey[accent];
  return (
    <button
      type={type ?? 'button'}
      aria-pressed={selected}
      // border-2 on both states keeps the layout stable; unselected uses a
      // subtle neutral border, selected swaps in the accent colour + ring.
      className={cn(
        'group flex-1 min-w-[110px] rounded-lg bg-surface-elevated shadow-sm',
        'border-2 px-3 py-2 text-start transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        selected
          ? cn(a.border, 'ring-2', a.ring)
          : 'border-border-subtle hover:border-border-default',
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            'text-xs font-medium uppercase tracking-wide truncate',
            selected ? a.text : 'text-text-muted',
          )}
        >
          {label}
        </span>
        <span
          aria-hidden="true"
          className={cn('inline-block h-2 w-2 rounded-full shrink-0', a.dot)}
        />
      </div>
      <div className="mt-1.5">
        {loading ? (
          <span
            className="inline-block h-7 w-10 rounded bg-sand-100 animate-pulse"
            aria-hidden="true"
          />
        ) : (
          <span className="text-2xl font-semibold text-text-primary tabular leading-none">
            {value}
          </span>
        )}
      </div>
    </button>
  );
}
